import { useState } from "react";
import Topbar from "@/components/Topbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Plus, PencilSimple, Copy, EyeSlash, Trash, DotsSixVertical, Eye, UploadSimple,
  FloppyDisk, CalendarBlank, Check,
} from "@phosphor-icons/react";
import { SERVICES, METRICS, COLLABORATEURS, formatValue } from "./perfDemoData";
import { PerfBanner, SectionTitle, SourceTag } from "./PerfUI";

export default function PerfAdminMockup() {
  return (
    <>
      <Topbar title="Administration Performance" subtitle="Saisie journalière · composition du tableau · indicateurs — réservé aux administrateurs" />
      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-[1600px] w-full" data-testid="perf-admin-mockup">
        <PerfBanner>
          Maquette visuelle à valider — rien n'est enregistré. Objectif : tout créer ici (onglets, services, indicateurs, objectifs, blocs) sans jamais toucher au code.
        </PerfBanner>

        <Tabs defaultValue="saisie">
          <TabsList className="bg-zinc-100 rounded-none p-1 h-auto flex-wrap justify-start mb-6" data-testid="perf-admin-tabs">
            <TabsTrigger value="saisie" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-admin-tab-saisie">Saisie journalière</TabsTrigger>
            <TabsTrigger value="composer" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-admin-tab-composer">Composer le tableau</TabsTrigger>
            <TabsTrigger value="catalogue" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-admin-tab-catalogue">Indicateurs & services</TabsTrigger>
            <TabsTrigger value="journal" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-admin-tab-journal">Journal</TabsTrigger>
          </TabsList>

          <TabsContent value="saisie"><SaisieTab /></TabsContent>
          <TabsContent value="composer"><ComposerTab /></TabsContent>
          <TabsContent value="catalogue"><CatalogueTab /></TabsContent>
          <TabsContent value="journal"><JournalTab /></TabsContent>
        </Tabs>
      </main>
    </>
  );
}

function SaisieTab() {
  const [byCollab, setByCollab] = useState(false);
  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-2 h-10 px-3 border border-zinc-300 bg-white text-sm">
          <CalendarBlank size={15} weight="bold" className="text-zinc-400" />
          <span className="font-mono">30/09/2026</span>
        </div>
        <div className="inline-flex border border-zinc-300 bg-white">
          <button onClick={() => setByCollab(false)} data-testid="saisie-mode-service" className={`px-4 h-10 text-sm ${!byCollab ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>Par service</button>
          <button onClick={() => setByCollab(true)} data-testid="saisie-mode-collab" className={`px-4 h-10 text-sm ${byCollab ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>Par collaborateur</button>
        </div>
        <div className="flex-1" />
        <Button className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-10 gap-2" data-testid="saisie-save">
          <FloppyDisk size={16} weight="bold" /> Enregistrer la journée
        </Button>
      </div>

      {byCollab ? (
        <div className="space-y-4">
          {COLLABORATEURS.map((c) => (
            <div key={c.id} className="bg-white border border-zinc-200">
              <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                <div className="font-heading font-semibold text-sm">{c.name}</div>
                <span className="label-tiny text-zinc-400">{SERVICES.find((s) => s.id === c.service)?.name}</span>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {METRICS.filter((m) => m.service === c.service && m.source === "MANUAL").map((m) => (
                  <Field key={m.id} metric={m} testId={`saisie-${c.id}-${m.id}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {SERVICES.map((s) => {
            const ms = METRICS.filter((m) => m.service === s.id);
            const manual = ms.filter((m) => m.source === "MANUAL");
            const auto = ms.filter((m) => m.source !== "MANUAL");
            return (
              <div key={s.id} className="bg-white border border-zinc-200" data-testid={`saisie-service-${s.id}`}>
                <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                  <div className="font-heading font-semibold text-sm">{s.name}</div>
                  <span className="label-tiny text-zinc-400">{manual.length} à saisir{auto.length ? ` · ${auto.length} automatique${auto.length > 1 ? "s" : ""}` : ""}</span>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {manual.map((m) => <Field key={m.id} metric={m} testId={`saisie-${m.id}`} />)}
                  {auto.map((m) => (
                    <div key={m.id} className="opacity-70">
                      <div className="label-tiny text-zinc-400 mb-1 flex items-center gap-1.5">{m.name} <SourceTag source={m.source} /></div>
                      <div className="h-10 border border-dashed border-zinc-300 bg-zinc-50 flex items-center px-3 text-sm text-zinc-500 font-mono">
                        {m.formula || "via connecteur"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function Field({ metric, testId }) {
  const [v, setV] = useState("");
  return (
    <div>
      <div className="label-tiny text-zinc-400 mb-1 flex items-center gap-1.5">
        {metric.name}
        <span className="text-zinc-300">·</span>
        <span className="normal-case text-zinc-400">{UNIT_LABEL[metric.unit]}</span>
      </div>
      <Input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder="—"
        inputMode="numeric"
        className="h-10 rounded-sm border-zinc-300 tabular-nums"
        data-testid={testId}
      />
    </div>
  );
}

const UNIT_LABEL = { number: "nombre", currency: "€", percent: "%", duration: "durée" };

const BLOCKS = [
  { id: "b1", type: "Cartes KPI", title: "Indicateurs clés", detail: "6 indicateurs · période suivie", size: "Pleine largeur" },
  { id: "b2", type: "Graphique", title: "Évolution expéditions / retours", detail: "Courbe · 7 derniers jours", size: "2/3" },
  { id: "b3", type: "Carte KPI", title: "% retour", detail: "Calculé · bas = mieux", size: "1/3" },
  { id: "b4", type: "Tableau", title: "Résultats par service", detail: "Colonnes : réalisé, objectif, atteinte, veille", size: "Pleine largeur" },
  { id: "b5", type: "Tableau", title: "Résultats par collaborateur", detail: "Masqué — historique conservé", size: "Pleine largeur", hidden: true },
];

function ComposerTab() {
  const [tab, setTab] = useState("global");
  const tabs = [
    { id: "global", label: "Vue globale" },
    { id: "flux", label: "Flux pièces" },
    { id: "qualite", label: "Qualité & SAV" },
    { id: "equipes", label: "Équipes", hidden: true },
  ];
  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
        <div className="flex items-center gap-2 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`composer-tab-${t.id}`}
              className={`h-9 px-3 text-xs sm:text-sm border flex items-center gap-2 ${
                tab === t.id ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-600 hover:bg-zinc-50"
              }`}
            >
              <DotsSixVertical size={13} weight="bold" className="opacity-50" />
              {t.label}
              {t.hidden && <EyeSlash size={12} />}
            </button>
          ))}
          <button className="h-9 px-3 text-xs sm:text-sm border border-dashed border-zinc-400 text-zinc-600 hover:bg-zinc-50 flex items-center gap-1.5" data-testid="composer-add-tab">
            <Plus size={13} weight="bold" /> Ajouter un onglet
          </button>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm h-9 border-zinc-300 gap-2 text-xs sm:text-sm" data-testid="composer-preview">
            <Eye size={15} weight="bold" /> Aperçu
          </Button>
          <Button className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-9 gap-2 text-xs sm:text-sm" data-testid="composer-publish">
            <UploadSimple size={15} weight="bold" /> Publier les modifications
          </Button>
        </div>
      </div>

      <div className="border border-amber-300 bg-amber-50 px-4 py-2.5 mb-5 text-xs text-amber-900 flex items-center gap-2">
        <span className="font-semibold">3 modifications en brouillon</span>
        <span>— invisibles par les collaborateurs jusqu'à publication.</span>
      </div>

      <div className="space-y-3" data-testid="composer-blocks">
        {BLOCKS.map((b) => (
          <div key={b.id} className={`bg-white border ${b.hidden ? "border-dashed border-zinc-300 opacity-70" : "border-zinc-200"} flex flex-col sm:flex-row sm:items-center gap-3 px-3 py-3`} data-testid={`composer-block-${b.id}`}>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button className="text-zinc-300 hover:text-zinc-600 cursor-grab shrink-0" title="Déplacer">
                <DotsSixVertical size={20} weight="bold" />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="label-tiny text-zinc-400">{b.type}</span>
                  <span className="text-[10px] text-zinc-400 border border-zinc-200 px-1.5">{b.size}</span>
                  {b.hidden && <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5">Masqué</span>}
                </div>
                <div className="font-medium text-sm truncate mt-0.5">{b.title}</div>
                <div className="text-xs text-zinc-500 truncate">{b.detail}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <IconBtn icon={PencilSimple} label="Modifier" />
              <IconBtn icon={Copy} label="Dupliquer" />
              <IconBtn icon={EyeSlash} label="Masquer" />
              <IconBtn icon={Trash} label="Supprimer" danger />
            </div>
          </div>
        ))}
        <button className="w-full border border-dashed border-zinc-400 py-4 text-sm text-zinc-600 hover:bg-zinc-50 flex items-center justify-center gap-2" data-testid="composer-add-block">
          <Plus size={16} weight="bold" /> Ajouter un bloc — carte KPI, tableau ou graphique
        </button>
      </div>
    </>
  );
}

const IconBtn = ({ icon: Icon, label, danger }) => (
  <button
    title={label}
    className={`w-8 h-8 border border-zinc-200 flex items-center justify-center transition-colors ${
      danger ? "text-zinc-400 hover:text-red-600 hover:border-red-300" : "text-zinc-500 hover:text-zinc-900 hover:border-zinc-400"
    }`}
  >
    <Icon size={15} weight="bold" />
  </button>
);

function CatalogueTab() {
  return (
    <>
      <SectionTitle right={<Button variant="outline" className="rounded-sm h-9 border-zinc-300 gap-2 text-xs" data-testid="catalogue-add-service"><Plus size={14} weight="bold" /> Nouveau service</Button>}>
        Services
      </SectionTitle>
      <div className="flex flex-wrap gap-2 mb-2" data-testid="catalogue-services">
        {SERVICES.map((s) => (
          <span key={s.id} className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 h-9 text-sm">
            <Check size={13} weight="bold" className="text-green-600" />
            {s.name}
            <PencilSimple size={13} className="text-zinc-400" />
          </span>
        ))}
        <span className="inline-flex items-center gap-2 border border-dashed border-zinc-300 bg-zinc-50 px-3 h-9 text-sm text-zinc-500">
          Préparation moteurs <span className="text-[10px] text-zinc-400">(exemple futur)</span>
        </span>
      </div>

      <SectionTitle right={<Button variant="outline" className="rounded-sm h-9 border-zinc-300 gap-2 text-xs" data-testid="catalogue-add-metric"><Plus size={14} weight="bold" /> Nouvel indicateur</Button>}>
        Indicateurs
      </SectionTitle>
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="catalogue-metrics">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <Th>Indicateur</Th><Th>Service</Th><Th>Unité</Th><Th>Sens</Th><Th>Source</Th><Th right>Objectif / jour</Th><Th right>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m) => (
              <tr key={m.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                <td className="px-4 py-3 font-medium">
                  {m.name}
                  {m.formula && <div className="text-[10px] text-zinc-400 font-mono mt-0.5">{m.formula}</div>}
                </td>
                <td className="px-4 py-3 text-zinc-500">{SERVICES.find((s) => s.id === m.service)?.name}</td>
                <td className="px-4 py-3 text-zinc-500">{UNIT_LABEL[m.unit]}</td>
                <td className="px-4 py-3 text-zinc-500">{m.dir === "up" ? "Plus haut = mieux" : "Plus bas = mieux"}</td>
                <td className="px-4 py-3"><SourceTag source={m.source} /></td>
                <td className="px-4 py-3 text-right tabular-nums">{formatValue(m.target, m.unit)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <IconBtn icon={PencilSimple} label="Modifier" />
                    <IconBtn icon={EyeSlash} label="Désactiver" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-600">
        Désactiver un indicateur le retire du tableau et de la saisie mais conserve tout son historique. Les objectifs sont datés : modifier un objectif n'écrase pas les atteintes passées.
      </div>
    </>
  );
}

function JournalTab() {
  const rows = [
    { d: "30/09/2026 18:42", u: "Emanuel T.", a: "Saisie du jour enregistrée", t: "9 services · 24 valeurs" },
    { d: "30/09/2026 18:05", u: "Emanuel T.", a: "Publication du tableau", t: "Onglet « Qualité & SAV » · 2 blocs ajoutés" },
    { d: "29/09/2026 09:12", u: "Emanuel T.", a: "Objectif modifié", t: "% retour : 5 % → 4,5 % (à partir du 29/09)" },
    { d: "28/09/2026 16:30", u: "Emanuel T.", a: "Indicateur créé", t: "« % litige » · calculé" },
  ];
  return (
    <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="perf-journal">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50"><Th>Date</Th><Th>Utilisateur</Th><Th>Action</Th><Th>Détail</Th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.d} className="border-b border-zinc-100 last:border-0">
              <td className="px-4 py-3 font-mono text-xs text-zinc-500">{r.d}</td>
              <td className="px-4 py-3">{r.u}</td>
              <td className="px-4 py-3 font-medium">{r.a}</td>
              <td className="px-4 py-3 text-zinc-500">{r.t}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`label-tiny text-zinc-400 font-medium px-4 py-3 ${right ? "text-right" : "text-left"}`}>{children}</th>
);
